import React, {
    useState, useEffect, useRef, useMemo,
} from 'react'
import {
    AnalyticsEvent, AppData, DynamicContentType, CustomEventType, MediaType, PageType, PromptOptions, ThumbSize, OrganizationData, RootState,
} from 'app/types'
import {
    Media, SlideShow, WaterMark, Button, Message,
} from 'components'
import { IntersectionHelper } from 'views/DynamicPage/IntersectionHelper'
import { Link } from 'react-router-dom'
import { showPrompt } from 'actions/appActions'
import {
    useAdminPermissions, useAppDispatch, useAppSelector, useDynamicContent, useHybridContent, usePage,
} from 'app/hooks'
import { Provinces, referralSources } from 'app/constants'
import { organizationContent, organizationStaticContent, projectStaticContent } from 'app/transformers'
import { logger } from 'helpers/logger'
import * as fnc from 'helpers/fnc'
import { DynamicContent } from './DynamicContent'

interface DynamicContentViewProps {
    app: AppData,
    organization: OrganizationData,
    link: string
    show: boolean,
    mobile: boolean,
    onSelect,
    required: boolean,
    previewMode?: boolean,
}

const waterfallContent = new Set(['footer'])

export function DynamicContentView(props: DynamicContentViewProps) {
    const {
        app: appProp, builder, organization, link, show, mobile, onSelect, required, previewMode, ...otherProps
    } = props
    const customViewRef = useRef(null)
    const page = usePage()
    const adminPermissions = useAdminPermissions()
    const dispatch = useAppDispatch()
    const editInline = useAppSelector((state: RootState) => state.admin.editInline)
    const hybridContent = useHybridContent()
    const dynamicContent = useDynamicContent()

    // 🎯 FIX: Use the most up-to-date app data from Redux state
    // Priority order: admin.project -> app.projectCurrent[0] -> prop
    const appFromState = useAppSelector((state: RootState) => state.app.projectCurrent[0])
    const appFromAdmin = useAppSelector((state: RootState) => state.admin.project)

    // Use admin project if it exists and matches the app ID, otherwise use state, then prop as fallback
    const app = useMemo(() => {
        const appPropId = appProp?.meta?.id || appProp?.id
        const adminProjectId = appFromAdmin?.meta?.id || appFromAdmin?.id
        const stateProjectId = appFromState?.meta?.id || appFromState?.id

        if (appFromAdmin && adminProjectId && adminProjectId === appPropId) {
            return appFromAdmin
        } if (appFromState && stateProjectId && stateProjectId === appPropId) {
            return appFromState
        }
        return appProp
    }, [appProp, appFromState, appFromAdmin, link])

    // Add explicit dependency on galleries to trigger re-render when they change
    const galleries = useAppSelector((state: RootState) => {
        if (app) {
            return app.galleries
        } if (organization) {
            return organization.galleries
        }
        return []
    })

    // Use a key based on galleries length and content to force re-evaluation
    const galleriesKey = useMemo(() => {
        const key = galleries ? galleries.map((g) => `${g.id}-${g.media?.length || 0}`).join(',') : ''
        return key
    }, [galleries, link, app])

    useEffect(() => {
        calculateShift()
        window.addEventListener('resize', calculateShift)
        return () => window.removeEventListener('resize', calculateShift)
    }, [])

    function calculateShift() {
        // Get the width of the customView vs the screen size to see how much auto margin has been applied
        if (customViewRef.current) {
            const rect = customViewRef.current.getBoundingClientRect()

            // Get the current zoom value using the helper function
            const zoomValue = fnc.getAppZoom()

            // Adjust for zoom - getBoundingClientRect returns zoomed dimensions
            const customViewWidth = rect.width * zoomValue
            const screenWidth = window.innerWidth
            const shift = (screenWidth - customViewWidth) / 2

            document.documentElement.style.setProperty('--shift', `${shift}px`)
        }
    }

    let customContent = null

    // Determine content source
    if (app && app.link in projectStaticContent) {
        customContent = projectStaticContent[app.link]
    }

    if (organization && (!customContent || (waterfallContent.has(link) && !(link in customContent))) && !(app?.dynamicContent?.find((x) => x.link == link))) {
        customContent = organizationStaticContent(organization)
    }

    // If no static content found, check for dynamic content from the database for this page
    const hasDynamicContent = useMemo(() => {
        if (customContent) {
            return false // Already have static content
        }

        // Check if there's dynamic content for this page in the database
        const pageContent = app?.dynamicContent?.filter((x) => x.pageId === page?.id) || []
        return pageContent.length > 0
    }, [customContent, app?.dynamicContent, page?.id])

    // Force re-computation of finalComponents when galleries change
    const finalComponents = useMemo(() => {
        // If no static content but we have dynamic content, pass empty object to allow dynamic content processing
        const contentToProcess = customContent || (hasDynamicContent ? {} : null)

        const components = hybridContent(app, builder, organization, { ...(props.page || page), link }, contentToProcess, previewMode)
        return components
    }, [app, builder, organization, page, props.page, link, customContent, hasDynamicContent, previewMode, galleriesKey, hybridContent])

    let isAdmin = false
    if (adminPermissions && (app || organization)) {
        isAdmin = adminPermissions(app, organization)
    }
    const filterDraft = (x) => {
        if (x.draft && !isAdmin) {
            return false
        }
        return true
    }
    const headerComponent = finalComponents.find((x) => x.dynamicContentTypeId == DynamicContentType.Header && filterDraft(x))
    const bodyComponents = finalComponents.filter((x) => x.dynamicContentTypeId != DynamicContentType.Header && filterDraft(x))
    const sortedBody = bodyComponents.sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order
        return String(a.id || '').localeCompare(String(b.id || ''))
    })

    const bodyElem = sortedBody.map((x, ix) => <DynamicContent key={`${x.id || x.order}-${galleriesKey}`} idx={x.order} app={app} organization={organization} {...x} {...otherProps} />)

    function handleAddFooterColumn() {
        window.dispatchEvent(new CustomEvent(CustomEventType.ContentAction, { detail: { action: 'add', data: { dynamicContentTypeId: DynamicContentType.Footer, order: bodyComponents.length }, page: props.page } }))
    }
    const isFooter = page?.pageType == PageType.Footer || link == 'footer'
    if (finalComponents.length == 0 && isFooter && required) {
        const test = hybridContent(app, builder, organization, { ...(props.page || page), link }, customContent, true)
        return <Message type="error" info="No content found" />
    }

    if (isFooter) {
        return <React.Fragment>
            {bodyElem}
            {editInline && <Button onClick={handleAddFooterColumn}>Add Column</Button>}
        </React.Fragment>
    }

    return <React.Fragment>
        <IntersectionHelper />
        {headerComponent && <DynamicContent key={`header-${galleriesKey}`} idx={headerComponent.order} app={app} organization={organization} {...headerComponent} {...otherProps} />}
        <div id="custom-view" className="content-view" key={link} data-link={link} data-page-type={(props.page || page)?.pageType} ref={customViewRef}>
            {bodyElem}
        </div>
    </React.Fragment>
}
